import { WarningOutlined } from "@ant-design/icons";
import { TIMESERIES_TOP_ORDER } from "@const/timeseriesRequest.contants";
import { TimeseriesTopOrder } from "@dto/timeseriesRequest.dto";
import { Button, InputNumber, ModalFuncProps, Slider, SliderSingleProps, Typography } from "antd";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { XmModal } from "src/component/modal/XmModal";
import { XmSelect } from "src/component/select/XmSelect";
import { XmAsyncButton } from "../../asyncButton/XmAsyncButton";

interface TopItemsModalState extends ModalFuncProps {
  order: TimeseriesTopOrder;
  count: number;
  maxCount: number;
  onConfirm: (count: number, order: TimeseriesTopOrder) => Promise<void>;
  onCancel: (...args: unknown[]) => unknown;
}

/** Items above this count are loaded in coarser steps and warned about. */
const FINE_STEP_LIMIT = 200;
const WARNING_COLOR = 'var(--xm-color-warning)';
const SLOW_LOAD_WARNING = `Loading more than ${FINE_STEP_LIMIT} items can take a long time and slow down the chart.`;

/**
 * Non-linear slider scale - fine steps for small counts, coarser ones for high counts:
 * 1-200 by 1, up to 1000 by 10, up to 10000 by 100, and so on.
 */
const buildScale = (max: number): number[] => {
  const values: number[] = [];
  let value = 1, step = 1, boundary = FINE_STEP_LIMIT;
  while (value < max) {
    values.push(value);
    value += step;
    if (value > boundary) {
      boundary = boundary === FINE_STEP_LIMIT ? 1000 : boundary * 10;
      step *= 10;
      value = Math.ceil(value / step) * step;
    }
  }
  if (values.at(-1) !== max) values.push(max);
  return values;
};

const closestIndex = (values: number[], value: number): number => values
  .reduce((best, v, i) => Math.abs(v - value) < Math.abs(values[best] - value) ? i : best, 0);

export const TopItemsModal: FC<TopItemsModalState> = ({ maxCount, count, onConfirm, ...props }) => {
  const currentCount = useMemo(() => Math.min(maxCount, count), [count, maxCount]);
  const [nextItems, setNextItems] = useState(0);
  const [order, setOrder] = useState(props.order);
  const scale = useMemo(() => buildScale(maxCount), [maxCount]);
  const marks = useMemo(() => {
    const result: NonNullable<SliderSingleProps['marks']> = {
      // the total sits above the rail (antd puts every mark below it), clear of the boundary marks
      [scale.length - 1]: {
        style: { top: '-2.6em', ...maxCount > FINE_STEP_LIMIT && { color: WARNING_COLOR } },
        label: <span style={{ marginLeft: '-0.5em' }}>{maxCount}</span>
      }
    };
    scale.forEach((value, index) => {
      // step boundaries (200, 1000, 10000, ...), skipped when too close to the max label
      const isBoundary = value === FINE_STEP_LIMIT || (value >= 1000 && value % 1000 === 0 && Math.log10(value / 1000) % 1 === 0);
      // warning colored: past each one the slider steps coarser and the load gets slower
      if (isBoundary && index < scale.length - 15)
        result[index] = { style: { color: WARNING_COLOR }, label: value };
    });
    return result;
  }, [scale, maxCount]);

  const sliderIndex = closestIndex(scale, nextItems);
  const dragValue = useRef<HTMLSpanElement>(null);
  // written imperatively (no state), so the warning color is applied the same way
  const setDragValue = (value?: number) => {
    const el = dragValue.current;
    if (!el) return;
    el.textContent = value === undefined ? '' : `→ ${value}`;
    el.style.color = value !== undefined && value > FINE_STEP_LIMIT ? WARNING_COLOR : '';
  };

  // Initialized when the modal opens - a count update arriving while it is open must not stomp the picked value
  useEffect(() => {
    if (props.open) setNextItems(currentCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  return <XmModal title="Load items" closable={false} footer={<>
    <Button onClick={e => props.onCancel()}>Cancel</Button>
    <XmAsyncButton type='primary' onClickAsync={e => onConfirm(nextItems, order)}
      icon={nextItems > FINE_STEP_LIMIT ? <WarningOutlined /> : undefined}>Load {nextItems}</XmAsyncButton>
    <XmAsyncButton type='primary' onClickAsync={e => onConfirm(maxCount, order)}
      className={maxCount > FINE_STEP_LIMIT ? 'xm-btn-warning' : undefined}
      icon={maxCount > FINE_STEP_LIMIT ? <WarningOutlined /> : undefined}
      title={maxCount > FINE_STEP_LIMIT ? SLOW_LOAD_WARNING : undefined}>Load all {maxCount}</XmAsyncButton>
  </>} onOk={() => onConfirm(nextItems, order)} {...props}>
    <label>Top items to load: <InputNumber<number> min={1} max={maxCount} value={nextItems} onChange={setNextItems} /></label>
    {/* hidden longest readout holds the width, so the live value overlaying it never shifts the row */}
    <span style={{ position: 'relative', marginLeft: '0.5em', color: 'gray', fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ visibility: 'hidden' }}>&rarr; {maxCount}</span>
      <span ref={dragValue} style={{ position: 'absolute', left: 0, top: 0, whiteSpace: 'nowrap' }} />
    </span>
    <label style={{ marginLeft: '1em' }}>Order by: <XmSelect ignoreReadonly defaultValue={order} onChange={(value, option) => setOrder(value)}
      options={Object.values(TIMESERIES_TOP_ORDER).map(o => ({ value: o, label: o.toUpperCase() }))} />
    </label>
    <label style={{ margin: '0.5em 0 0 1em', color: 'gray', float: 'right', }}>Currently loaded: {currentCount || maxCount}</label>
    <Slider key={sliderIndex} min={0} max={scale.length - 1} defaultValue={sliderIndex}
      style={{ marginTop: '2em' }} // room for the max mark lifted above the rail
      onChange={index => setDragValue(scale[index])}
      onChangeComplete={index => { setNextItems(scale[index]); setDragValue(); }}
      tooltip={{ open: false }}
      marks={marks} />
    <Typography.Text type="warning">{nextItems > FINE_STEP_LIMIT ? <div>
      <WarningOutlined /> {SLOW_LOAD_WARNING}
    </div> : <br />}</Typography.Text>
  </XmModal>;
};